import { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { useConnectorTypeSchema } from '@/hooks/useConnectorTypes';
import { apiClient } from '@/lib/api/client';
import { splitFormData } from '@/lib/validation/splitFormData';
import { DEFAULT_SCHEDULE_TIMEZONE } from '@/constants/timezone';
import type { ConnectorType } from '@/types/connectors';
import type { ConnectorConfigFormData } from '../ConfigureConnector';
import type { HealthCheckResult } from '../../ConnectorHealthCheck';
import { validateFormData } from './wizardValidation';
import { getWizardSteps } from './wizardSteps';

const INITIAL_FORM: ConnectorConfigFormData = {
  name: '', description: '', enabled: true, schedule_enabled: false,
};

const toTypeName = (type: string) =>
  type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export const useCreateConnectorWizard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState<ConnectorType | null>(null);
  const [formData, setFormData] = useState<ConnectorConfigFormData>(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ConnectorConfigFormData, string>>>({});
  const [healthCheckResult, setHealthCheckResult] = useState<HealthCheckResult | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  // The last create failure, kept until the next attempt. The toast alone
  // auto-dismisses, so a rejected create could leave Review with no trace (#2056).
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState(false);
  // Mirrors `created` but updates synchronously so useBlocker (which evaluates
  // its predicate during navigation, before React re-renders) does not block the
  // post-create redirect. See spec D2a (#303).
  const createdRef = useRef(false);

  const { data: schema } = useConnectorTypeSchema(selectedType ?? '', !!selectedType);

  // The Schedule step (#273) is dropped for types that don't support
  // scheduling; default to present until the schema loads (it arrives well
  // before the Schedule step is reachable). IDs are positions in this list.
  const supportsScheduling = schema?.supports_scheduling ?? true;
  const stepDefs = useMemo(() => getWizardSteps(supportsScheduling), [supportsScheduling]);
  const totalSteps = stepDefs.length;
  const currentStepDef = stepDefs[currentStep - 1] ?? stepDefs[0];
  const currentStepKey = currentStepDef?.key;
  const testStepId = stepDefs.find((s) => s.key === 'test')?.id ?? 3;

  const selectType = useCallback((type: ConnectorType) => {
    setSelectedType(type);
    const previousAutoName = selectedType ? toTypeName(selectedType) : '';
    if (!formData.name || formData.name === previousAutoName) {
      setFormData((prev) => ({ ...prev, name: toTypeName(type) }));
    }
  }, [selectedType, formData.name]);

  const updateForm = useCallback((data: ConnectorConfigFormData) => {
    setFormData(data);
    setFormErrors({});
  }, []);

  const goNext = useCallback(() => {
    const { isValid, errors } = validateFormData(selectedType, formData, currentStepKey, schema);
    if (!isValid) {
      setFormErrors(errors);
      const entries = Object.entries(errors);
      toast({
        title: 'Validation Error',
        description: entries.length
          ? entries.map(([field, msg]) => `${field}: ${msg}`).join('; ')
          : 'Please fix the errors before proceeding',
        variant: 'destructive',
      });
      return;
    }
    setFormErrors({});
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  }, [currentStepKey, totalSteps, selectedType, formData, schema, toast]);

  // Leaving Review to edit makes the last failure stale, so drop it.
  const goBack = useCallback(() => {
    setCreateError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);
  const goToStep = useCallback((step: number) => {
    setCreateError(null);
    setCurrentStep(step);
  }, []);
  const completeTest = useCallback((result: HealthCheckResult) => setHealthCheckResult(result), []);

  const skipTest = useCallback(() => {
    setHealthCheckResult({
      status: 'unhealthy',
      checks: { connectorLibrary: 'ok', configuration: 'ok', connectivity: 'failed' },
      details: { skipped: true },
      timestamp: new Date().toISOString(),
    });
    // Advance to the step after Test (Schedule when present, else Review).
    setCurrentStep(testStepId + 1);
  }, [testStepId]);

  const create = useCallback(async () => {
    if (!selectedType) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      const { credentials, settings } = schema
        ? splitFormData(formData as unknown as Record<string, unknown>, schema.fields)
        : { credentials: {}, settings: {} };

      const config = {
        connector_type: selectedType,
        name: formData.name,
        description: formData.description || undefined,
        enabled: formData.enabled,
        credentials,
        // Only the schema's own fields. No settings model declares max_workers
        // (workers come from <TYPE>_MAX_WORKERS), and fixture forbids extras (#2056).
        settings,
        schedule_enabled: formData.schedule_enabled || false,
        schedule_cron: formData.schedule_cron || undefined,
        schedule_timezone: formData.schedule_timezone || DEFAULT_SCHEDULE_TIMEZONE,
        query_file_yaml: formData.queryFile?.content || undefined,
      };

      const response = await apiClient.post('/connectors/configurations', config);

      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      queryClient.invalidateQueries({ queryKey: ['sources'] });

      toast({ title: 'Connector Created', description: `${formData.name} has been created successfully` });

      // #1060: the create is accepted even when a required setting is absent, but
      // the connector will fail at run until those fields are set. Surface them as
      // a separate, non-blocking notice so the user can complete the config.
      const settingsWarnings: Array<{ field: string; message: string }> =
        response.data?.settings_warnings ?? [];
      if (settingsWarnings.length > 0) {
        toast({
          title: 'Incomplete configuration',
          description: `Created, but missing required settings: ${settingsWarnings
            .map((w) => w.field)
            .join(', ')}. The connector will fail until these are set.`,
        });
      }

      // Mark created BEFORE navigating so the unsaved-changes guard does not
      // prompt on the post-create redirect (state for beforeunload/UI, ref for
      // the synchronous useBlocker predicate).
      createdRef.current = true;
      setCreated(true);

      const createdConnector = response.data ?? {};
      const typeForUrl = createdConnector.connector_type ?? selectedType;
      const nameForUrl = createdConnector.name ?? formData.name;
      navigate(`/connectors/${encodeURIComponent(typeForUrl)}/${encodeURIComponent(nameForUrl)}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      setCreateError(message);
      toast({
        title: 'Failed to Create Connector',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  }, [selectedType, formData, schema, queryClient, toast, navigate]);

  const steps = stepDefs.map((s) => ({ ...s, isComplete: currentStep > s.id, isActive: currentStep === s.id }));
  const isLastStep = currentStep >= totalSteps;
  const canProceed =
    currentStepKey === 'type'
      ? selectedType !== null
      : currentStepKey === 'test'
        ? healthCheckResult !== null
        : true; // 'configure' validates on Continue; 'schedule' is skippable

  // Dirty once a type is selected and the connector has not yet been created.
  // Drives the unsaved-changes guard; cleared by `created` so the success
  // redirect is never blocked.
  const isDirty = selectedType !== null && !created;

  // Synchronous dirty check for useBlocker (reads the ref, not state) so the
  // post-create redirect is never blocked (#303).
  const getIsDirty = useCallback(
    () => selectedType !== null && !createdRef.current,
    [selectedType],
  );

  return {
    currentStep, currentStepKey, selectedType, formData, formErrors, healthCheckResult, isCreating, createError, schema,
    steps, canProceed, isLastStep, isDirty, getIsDirty,
    selectType, updateForm, goNext, goBack, goToStep, completeTest, skipTest, create,
  };
};
